/**
 * cdn-domain-waf-tab.tsx — WAF configuration tab for a CDN domain (act-as).
 *
 * Shows WAF enable/disable toggle, mode (detect/prevent), sensitivity, and
 * managed ruleset toggles. Mutations gated through ActAsMutationConfirm.
 *
 * Endpoints: GET/PUT /api/v3/cdn/domains/:id/waf
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shield } from 'lucide-react';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  Button,
  Switch,
  Skeleton,
  Badge,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { TabError } from '@/pages/customers/detail/tab-states';
import {
  useActAsCDNWaf,
  actAsCDNSecurityKeys,
  type WafConfig,
  type WafRuleSet,
} from '@/api/act-as-cdn-security';
import { actAsClient } from '@/api/act-as-client';
import { ActAsMutationConfirm } from '../act-as-mutation-confirm';

const DEFAULT_RULESETS: WafRuleSet[] = [
  { id: 'owasp-crs', name: 'OWASP Core Rule Set', enabled: true, version: '3.3' },
  { id: 'sqli', name: 'SQL Injection Protection', enabled: true },
  { id: 'xss', name: 'Cross-Site Scripting (XSS)', enabled: true },
  { id: 'rce', name: 'Remote Code Execution', enabled: false },
  { id: 'lfi', name: 'Local File Inclusion', enabled: false },
  { id: 'scanner', name: 'Scanner Detection', enabled: false },
];

interface WafForm {
  enabled: boolean;
  mode: 'detect' | 'prevent';
  sensitivity: 'low' | 'medium' | 'high';
  rule_sets: WafRuleSet[];
}

const DEFAULTS: WafForm = {
  enabled: false,
  mode: 'detect',
  sensitivity: 'medium',
  rule_sets: DEFAULT_RULESETS,
};

function hydrate(cfg: WafConfig): WafForm {
  return {
    enabled: cfg.enabled ?? false,
    mode: cfg.mode ?? 'detect',
    sensitivity: cfg.sensitivity ?? 'medium',
    rule_sets: cfg.rule_sets?.length ? cfg.rule_sets : DEFAULT_RULESETS,
  };
}

interface Props { domainId: string }

export function CdnDomainWafTab({ domainId }: Props) {
  const { t } = useTranslation();
  const q = useActAsCDNWaf(domainId);
  const [form, setForm] = useState<WafForm>(DEFAULTS);
  const [snapshot, setSnapshot] = useState<WafForm>(DEFAULTS);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const next = q.data ? hydrate(q.data) : DEFAULTS;
    setForm(next);
    setSnapshot(next);
  }, [q.data]);

  const dirty = JSON.stringify(form) !== JSON.stringify(snapshot);
  const set = <K extends keyof WafForm>(k: K, v: WafForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const toggleRuleset = (id: string, enabled: boolean) =>
    setForm((f) => ({
      ...f,
      rule_sets: f.rule_sets.map((r) => (r.id === id ? { ...r, enabled } : r)),
    }));

  if (q.isLoading) return <Skeleton style={{ height: 320 }} />;
  if (q.isError) {
    return (
      <TabError
        title={t('cdn_domain.waf_tab.load_error', 'Failed to load WAF configuration')}
        onRetry={() => q.refetch()}
      />
    );
  }

  const payload: WafConfig = {
    enabled: form.enabled,
    mode: form.mode,
    sensitivity: form.sensitivity,
    rule_sets: form.rule_sets,
  };

  return (
    <Section
      title={t('cdn_domain.waf_tab.title', 'WAF')}
      description={t('cdn_domain.waf_tab.description', 'Web Application Firewall settings for this domain.')}
    >
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>

        {/* Enable + mode */}
        <Card>
          <CardHeader>
            <CardTitle>{t('cdn_domain.waf_tab.section_settings', 'WAF Settings')}</CardTitle>
            <CardDescription>
              {t('cdn_domain.waf_tab.section_settings_desc', 'Enable WAF and configure protection mode.')}
            </CardDescription>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
            <SettingRow
              title={t('cdn_domain.waf_tab.field_enabled', 'WAF enabled')}
              description={t('cdn_domain.waf_tab.field_enabled_desc', 'Activate the Web Application Firewall for this domain.')}
              control={<Switch checked={form.enabled} onCheckedChange={(v) => set('enabled', v)} />}
            />
            {form.enabled && (
              <>
                <SettingRow
                  title={t('cdn_domain.waf_tab.field_mode', 'Mode')}
                  description={t('cdn_domain.waf_tab.field_mode_desc', 'Detect logs matched requests; Prevent blocks them.')}
                  control={
                    <Select value={form.mode} onValueChange={(v) => set('mode', v as WafForm['mode'])}>
                      <SelectTrigger style={{ width: 160 }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="detect">{t('cdn_domain.waf_tab.mode_detect', 'Detect')}</SelectItem>
                        <SelectItem value="prevent">{t('cdn_domain.waf_tab.mode_prevent', 'Prevent')}</SelectItem>
                      </SelectContent>
                    </Select>
                  }
                />
                <SettingRow
                  title={t('cdn_domain.waf_tab.field_sensitivity', 'Sensitivity')}
                  description={t('cdn_domain.waf_tab.field_sensitivity_desc', 'Higher sensitivity catches more threats but may increase false positives.')}
                  control={
                    <Select value={form.sensitivity} onValueChange={(v) => set('sensitivity', v as WafForm['sensitivity'])}>
                      <SelectTrigger style={{ width: 160 }}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t('cdn_domain.waf_tab.sensitivity_low', 'Low')}</SelectItem>
                        <SelectItem value="medium">{t('cdn_domain.waf_tab.sensitivity_medium', 'Medium')}</SelectItem>
                        <SelectItem value="high">{t('cdn_domain.waf_tab.sensitivity_high', 'High')}</SelectItem>
                      </SelectContent>
                    </Select>
                  }
                />
              </>
            )}
          </CardBody>
        </Card>

        {/* Managed rulesets */}
        {form.enabled && (
          <Card>
            <CardHeader>
              <CardTitle>{t('cdn_domain.waf_tab.section_rulesets', 'Managed Rule Sets')}</CardTitle>
              <CardDescription>
                {t('cdn_domain.waf_tab.section_rulesets_desc', 'Toggle individual managed rule sets.')}
              </CardDescription>
            </CardHeader>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
              {form.rule_sets.length === 0 ? (
                <EmptyState
                  size="sm"
                  icon={<Shield size={20} />}
                  title={t('cdn_domain.waf_tab.no_rulesets', 'No rule sets available')}
                />
              ) : (
                form.rule_sets.map((rs) => (
                  <SettingRow
                    key={rs.id}
                    title={
                      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
                        {rs.name}
                        {rs.version && (
                          <Badge tone="neutral" appearance="subtle" size="sm">v{rs.version}</Badge>
                        )}
                      </span>
                    }
                    control={
                      <Switch
                        checked={rs.enabled}
                        onCheckedChange={(v) => toggleRuleset(rs.id, v)}
                      />
                    }
                  />
                ))
              )}
            </CardBody>
          </Card>
        )}

        <ActionBar
          end={
            <>
              <Button variant="secondary" disabled={!dirty} onClick={() => setForm(snapshot)}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button disabled={!dirty} onClick={() => setConfirmOpen(true)}>
                {t('cdn_domain.waf_tab.action_save', 'Save')}
              </Button>
            </>
          }
        />
      </div>

      <ActAsMutationConfirm
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={t('cdn_domain.waf_tab.confirm_save_title', 'Save WAF configuration')}
        summary={t('cdn_domain.waf_tab.save_summary', 'Update WAF mode, sensitivity, and managed rule sets for this domain.')}
        queryKeysToInvalidate={[actAsCDNSecurityKeys.waf(domainId)]}
        mutationFn={() => actAsClient.put(`/cdn/domains/${domainId}/waf`, payload)}
        onSuccess={() => setSnapshot(form)}
      />
    </Section>
  );
}
